import { useState, useRef, useEffect } from "react";
import { TextField, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";

export default function OTPInput({ otp, setOtp, handleSendOtp = () => {} }) {
  const otpLength = 6;
  const [timer, setTimer] = useState(30); // Timer starts at 30 seconds
  const [isResendEnabled, setIsResendEnabled] = useState(false); // Control resend button
  const inputsRef = useRef([]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendEnabled(true); // Enable resend button when timer hits 0
    }

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [timer]);

  const handleChange = (e, index) => {
    const { value } = e.target;
    const regex = /^[0-9]+$/;
    if (!regex.test(value)) return;
    if (value.length > 1) {
      if (value.length === otpLength) {
        // handle autocomplete from mobile keyboard
        setOtp(value.split(""));
        // Move to the next input if the value is filled
        inputsRef.current[otpLength - 1].focus();
        return;
      }
      if (index < otpLength - 1) {
        // handle partial removal then filling back in
        const newOtp = [...otp];
        const [, lastValue] = value.split("");
        newOtp[index + 1] = lastValue;
        setOtp(newOtp);
        inputsRef.current[index + 1].focus();
      }
      return;
    } // Only allow single characters
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to the next input if the value is filled
    if (value && index < otpLength - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    const { key } = e;

    if (key === "Backspace") {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);

      // Move focus to the previous input on backspace
      if (index > 0) {
        inputsRef.current[index - 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, otpLength);
    const newOtp = pasteData.split("");
    setOtp((prevOtp) => newOtp.map((char, index) => char || prevOtp[index]));

    // Move focus to the next empty input
    newOtp.forEach((char, index) => {
      if (char && inputsRef.current[index]) {
        inputsRef.current[index].focus();
      }
    });
  };

  const handleResend = () => {
    setTimer(30);
    setIsResendEnabled(false);
    handleSendOtp();
  };

  return (
    <Stack spacing={2}>
      <Stack
        spacing={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onPaste={handlePaste}
      >
        {otp.map((value, index) => (
          <TextField
            key={index}
            inputRef={(el) => {
              inputsRef.current[index] = el;
              return el;
            }}
            value={value}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            inputProps={{
              // maxLength: 2,
              style: {
                textAlign: "center", maxWidth: "60px", height: "60px", boxSizing: "border-box", fontSize: "clamp(12px, 2vw, 20px)"
              },
              inputMode: "numeric", // Helps for mobile keyboards
              pattern: "[0-9]*", // Restricts non-numeric input
            }}
            sx={{
              "& .MuiOutlinedInput-input": {
                padding: "14px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "black.main",
              },
              "& .MuiOutlinedInput-root": {
                color: "black.main",
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "black.main",
              }
            }}
          />

        ))}
      </Stack>
      <Typography>
        Didn&apos;t receive an OTP?
        {" "}
        {isResendEnabled ? (
          <Typography component="span" onClick={handleResend} sx={{ cursor: "pointer" }} color="primary.main" disabled={!isResendEnabled}>
            Resend OTP
          </Typography>
        ) : (
          <Typography component="span" color="primary.main">
            Try again in
            {" "}
            {timer}
            s
          </Typography>
        )}
      </Typography>
    </Stack>

  );
}

OTPInput.propTypes = {
  handleSendOtp: PropTypes.func,
  otp: PropTypes.array,
  setOtp: PropTypes.func
};
